import {defineStore} from 'pinia'
import {useApiRequestStore} from '../../ApiRequestStore/useApiRequestStore'
import {
  IMeRequestInterfaces,
} from '@toolify/server/src/controllers/UserController/validators/Me/types/IMeRequestInterfaces'
import {
  IGetMyWorkspacesRequestInterfaces,
} from '@toolify/server/src/controllers/UserController/validators/GetMyWorkspaces/types/IGetMyWorkspacesRequestInterfaces'
import {UserSettingName} from '@toolify/server/src/models/mongoose/UserModel/enum/UserSettingName'
import {
  IUpdateUserSettingRequestInterfaces,
} from '@toolify/server/src/controllers/UserController/validators/UpdateUserSettings/types/IUpdateUserSettingRequestInterfaces'
import {
  IUpdateUserNickRequestInterfaces,
} from '@toolify/server/src/controllers/UserController/validators/UpdateUserNick/types/IUpdateUserNickRequestInterfaces'
import {UserStatusType} from '@toolify/server/src/models/mongoose/UserModel/enum/UserStatusType'
import {
  IUpdateUserStatusRequestInterfaces,
} from '@toolify/server/src/controllers/UserController/validators/UpdateUserStatus/types/IUpdateUserStatusRequestInterfaces'
import {
  IPatchUserRequestInterfaces,
} from '@toolify/server/src/controllers/UserController/validators/PatchUser/types/IPatchUserRequestInterfaces'
import {mapResponseEntity} from '@toolify/client/src/modules/api/singletons/mapResponseEntity'
import {mapUserResponseInstance} from '@toolify/client/src/modules/api/singletons/mapUserResponseInstance'
import {
  IGetMembersByAllParentResourcesRequestInterfaces,
} from '@toolify/server/src/controllers/PermissionController/validators/GetMembersByAllParentResources/types/IGetMembersByAllParentResourcesRequestInterfaces'
import {
  ExperimentalFeatureFlagType,
} from '@toolify/server/src/models/mongoose/UserModel/enum/ExperimentalFeatureFlagType'
import {
  IUpdateUserExperimentalFeatureFlagRequestInterfaces,
} from '@toolify/server/src/controllers/UserController/validators/UpdateUserExperimentalFeatureFlag/types/IUpdateUserExperimentalFeatureFlagRequestInterfaces'
import {IDeleteMeRequestInterfaces} from '@toolify/server/src/controllers/UserController/validators/DeleteMe/types/IDeleteMeRequestInterfaces'
import {
  IUpdateUserMailRequestInterfaces,
} from '@toolify/server/src/controllers/UserController/validators/UpdateUserMail/types/IUpdateUserMailRequestInterfaces'
import {
  IUpdateUserPasswordRequestInterfaces,
} from '@toolify/server/src/controllers/UserController/validators/UpdateUserPassword/types/IUpdateUserPasswordRequestInterfaces'
import {
  IUpdateUserOnboardingRoleAndDiscoverRequestInterfaces,
} from '@toolify/server/src/controllers/UserController/validators/UpdateUserOnboardingRoleAndDiscover/types/IUpdateUserOnboardingRoleAndDiscoverRequestInterfaces'
import {
  ISkipUserOnboardingStepRequestInterfaces,
} from '@toolify/server/src/controllers/UserController/validators/SkipUserOnboardingStep/types/ISkipUserOnboardingStepRequestInterfaces'
import {
  ICompleteUserOnboardingRequestInterfaces,
} from '@toolify/server/src/controllers/UserController/validators/CompleteUserOnboarding/types/ICompleteUserOnboardingRequestInterfaces'
import {OnboardingUserRoleType} from '@toolify/server/src/models/mongoose/UserModel/enum/OnboardingUserRoleType'
import {OnboardingDiscoverSourceType} from '@toolify/server/src/models/mongoose/UserModel/enum/OnboardingDiscoverSourceType'
import {OnboardingStepType} from '@toolify/server/src/enum/OnboardingStepType'
import {
  IGetAvailableLlmToolsRequestInterfaces,
} from '@toolify/server/src/controllers/UserController/validators/GetAvailableLlmTools/types/IGetAvailableLlmToolsRequestInterfaces'

export const useMeApiStore = defineStore('meApi', {
  actions: {
    async me(): Promise<IMeRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      const {user} = await apiRequestStore.get<IMeRequestInterfaces>('/me', {
      }, {
        priority: 1,
      })
      return {
        user: mapResponseEntity(user),
      }
    },

    async getMyWorkspaces(skip = 0, limit = 20): Promise<IGetMyWorkspacesRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      return await apiRequestStore.get<IGetMyWorkspacesRequestInterfaces>('/me/workspaces', {
        params: {
          skip,
          limit,
        },
      })
    },

    async updateSetting(settingName: UserSettingName, value: unknown): Promise<IUpdateUserSettingRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      return await apiRequestStore.put<IUpdateUserSettingRequestInterfaces>(`/me/setting/${settingName}`, {
        data: {
          value,
        },
      })
    },

    async getAvailableLlmTools(): Promise<IGetAvailableLlmToolsRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      return await apiRequestStore.get<IGetAvailableLlmToolsRequestInterfaces>('/me/available-llm-tools')
    },

    async updateExperimentalFeatureFlag(experimentalFeatureFlagType: ExperimentalFeatureFlagType, value: boolean): Promise<IUpdateUserExperimentalFeatureFlagRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      return await apiRequestStore.put<IUpdateUserExperimentalFeatureFlagRequestInterfaces>(`/me/experimental-feature-flag/${experimentalFeatureFlagType}`, {
        data: {
          value,
        },
      })
    },

    async updateNick(nick: string): Promise<void> {
      const apiRequestStore = useApiRequestStore()
      await apiRequestStore.put<IUpdateUserNickRequestInterfaces>('/me/nick', {
        data: {
          nick,
        },
      })
    },

    async updateMail(mail: string, twoFactor?: {verificationId: string, code: string}): Promise<void> {
      const apiRequestStore = useApiRequestStore()
      await apiRequestStore.put<IUpdateUserMailRequestInterfaces>('/me/mail', {
        data: {
          mail,
          ...(twoFactor && {
            twoFactorVerificationId: twoFactor.verificationId,
            twoFactorCode: twoFactor.code,
          }),
        },
      })
    },

    async updatePassword(oldPassword: string, password: string, twoFactor?: {verificationId: string, code: string}): Promise<void> {
      const apiRequestStore = useApiRequestStore()
      await apiRequestStore.put<IUpdateUserPasswordRequestInterfaces>('/me/password', {
        data: {
          oldPassword,
          password,
          ...(twoFactor && {
            twoFactorVerificationId: twoFactor.verificationId,
            twoFactorCode: twoFactor.code,
          }),
        },
      })
    },

    async updateStatus(statusText: string, statusType: UserStatusType): Promise<void> {
      const apiRequestStore = useApiRequestStore()
      await apiRequestStore.put<IUpdateUserStatusRequestInterfaces>('/me/status', {
        data: {
          statusText,
          statusType,
        },
      })
    },

    async patchUser(lastChangelogVisitedAt: IPatchUserRequestInterfaces['body']['lastChangelogVisitedAt']): Promise<void> {
      const apiRequestStore = useApiRequestStore()
      await apiRequestStore.patch<IPatchUserRequestInterfaces>('/me', {
        data: {
          lastChangelogVisitedAt,
        },
      })
    },

    async updateAvatar(file: File|Blob): Promise<void> {
      const apiRequestStore = useApiRequestStore()
      const formData = new FormData()
      formData.append('file', file)
      await apiRequestStore.put('/me/avatar', {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        data: formData,
      })
    },

    async updateCover(file: File|Blob): Promise<void> {
      const apiRequestStore = useApiRequestStore()
      const formData = new FormData()
      formData.append('file', file)
      await apiRequestStore.put('/me/cover', {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        data: formData,
      })
    },

    async getFriends(): Promise<IGetMembersByAllParentResourcesRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      const {users} = await apiRequestStore.get<IGetMembersByAllParentResourcesRequestInterfaces>('/me/friends')
      return {
        users: users.map(user => {
          return mapUserResponseInstance(user)
        }),
      }
    },

    async deleteAccount(): Promise<void> {
      const apiRequestStore = useApiRequestStore()
      await apiRequestStore.delete<IDeleteMeRequestInterfaces>('/me')
    },

    async updateOnboardingRoleAndDiscover(role?: OnboardingUserRoleType, discoverSource?: OnboardingDiscoverSourceType): Promise<void> {
      const apiRequestStore = useApiRequestStore()
      await apiRequestStore.put<IUpdateUserOnboardingRoleAndDiscoverRequestInterfaces>('/me/onboarding/role-and-discover', {
        data: {
          ...(role && {role}),
          ...(discoverSource && {discoverSource}),
        },
      })
    },

    async skipOnboardingStep(stepType: OnboardingStepType): Promise<void> {
      const apiRequestStore = useApiRequestStore()
      await apiRequestStore.put<ISkipUserOnboardingStepRequestInterfaces>(`/me/onboarding/skip/${stepType}`, {})
    },

    async completeOnboarding(): Promise<void> {
      const apiRequestStore = useApiRequestStore()
      await apiRequestStore.put<ICompleteUserOnboardingRequestInterfaces>('/me/onboarding/complete', {})
    },
  },
})
